// src/lib/echartsTheme.ts
//
// The ECharts themes, registered once at module load from `main.tsx`.
//
// ─── WHAT THIS FILE ABSORBED ────────────────────────────────────────────────
// There used to be TWO chart themes in this repo and the app was running the
// thinner one. `lib/echarts.ts` built a full theme — tooltip confinement, a
// `visualMap` on the brand diverging ramp, dataZoom/radar/parallel label
// colours, per-axis-TYPE keying, ink computed against the mark — and registered
// it as `fintela-dark` / `fintela-light`. No component ever asked for those
// names, and nothing imported the module, so `registerTheme` never even ran.
// Meanwhile this file registered `internal-*` with roughly a sixth of that
// coverage, and every gap fell through to ECharts' own defaults: axis labels at
// #54555a (about 2.4:1 on the dark canvas), an unconfined tooltip cut in half
// by the card it is anchored in, and a blue-red `visualMap` from no palette we
// own.
//
// The two are now one registration under the `internal-*` names. Nothing in the
// app changes its theme string; the charts simply stop falling back.
//
// ─── AXES ARE KEYED BY TYPE, NOT BY ORIENTATION ─────────────────────────────
// `AxisModel.mergeDefaultAndTheme` reads `theme[axisType + 'Axis']` — so
// `categoryAxis` / `valueAxis` / `logAxis` / `timeAxis` are the keys that work
// and `xAxis` / `yAxis` are silently ignored. Keying by type is also what makes
// a horizontal bar chart come out right, where the category axis is y.

import * as echarts from 'echarts'
import {
  CHART_CHROME, CHART_SERIES, DIVERGING, type ChartChrome, type Mode,
} from '@/theme/chart'
import { neutral } from '@/theme/brand'
import { contrast } from '@/theme/paletteChecks'
import { SANS } from '@/theme/typography'

/** The theme name for a mode. The ONE place these strings are spelled. */
export function chartThemeName(mode: Mode): string {
  return `internal-${mode}`
}

// ─── Ink drawn ON a mark (pie, treemap, heatmap labels) ─────────────────────

/** Ink for a light fill. The darkest neutral in the brand ramp. */
export const ON_FILL_DARK = neutral[900]
/** Ink for a dark fill. */
export const ON_FILL_LIGHT = neutral[0]

/**
 * Label colour for text painted directly on a filled mark.
 *
 * Neither `palette.mode` nor the registered theme can decide this: the contrast
 * that matters is against the MARK, not against the canvas. A hardcoded white
 * disappears on the pale end of a scale and a hardcoded near-black disappears
 * on the dark end, so the fill itself has to choose.
 *
 * It MEASURES rather than thresholding on luminance. A 0.5 threshold is correct
 * at a palette's endpoints and picks the wrong ink outright in the mid-tones —
 * and these palettes are mostly mid-tones. Measuring considers the same two
 * candidates and can never do worse.
 *
 * `contrast` comes from `theme/paletteChecks`, which is the same function
 * `brand.test.ts` measures the palette with. A second copy of the luminance
 * maths lived here before and is exactly the kind of thing that drifts.
 */
export function onFill(fill: string): string {
  return contrast(ON_FILL_DARK, fill) >= contrast(ON_FILL_LIGHT, fill)
    ? ON_FILL_DARK
    : ON_FILL_LIGHT
}

// ─── Diverging heatmap ramp ─────────────────────────────────────────────────

/** Five stops subsampled from the shared diverging scale, for `norm` ∈ [-1, 1]. */
export function heatmapStops(mode: Mode): Array<[number, string]> {
  const d = DIVERGING[mode]
  return [
    [-1, d[0]],
    [-0.5, d[2]],
    [0, d[4]],
    [0.5, d[6]],
    [1, d[8]],
  ]
}

function lerpHex(a: string, b: string, t: number): string {
  const pa = [1, 3, 5].map((i) => parseInt(a.slice(i, i + 2), 16))
  const pb = [1, 3, 5].map((i) => parseInt(b.slice(i, i + 2), 16))
  const c = pa.map((v, i) => Math.round(v + (pb[i] - v) * t))
  return `#${c.map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

/**
 * Interpolate a colour out of an ascending `[position, hex]` ramp.
 *
 * Pairs with {@link onFill}: a cell's LABEL takes `onFill(heatmapFill(v, mode))`
 * so the ink is computed from the same interpolation as the fill under it and
 * the two cannot drift. One static label colour goes illegible at the saturated
 * ends of a diverging ramp; that is the defect this pairing avoids.
 */
export function rampFill(stops: Array<[number, string]>, value: number): string {
  const lo = stops[0][0]
  const hi = stops[stops.length - 1][0]
  const v = Math.max(lo, Math.min(hi, value))
  for (let i = 0; i < stops.length - 1; i += 1) {
    const [p0, c0] = stops[i]
    const [p1, c1] = stops[i + 1]
    // A zero-width segment would divide by zero; take its right-hand colour.
    if (v <= p1) return p1 === p0 ? c1 : lerpHex(c0, c1, (v - p0) / (p1 - p0))
  }
  return stops[stops.length - 1][1]
}

/** The fill a `visualMap` over {@link heatmapStops} paints for `norm` ∈ [-1, 1]. */
export function heatmapFill(norm: number, mode: Mode): string {
  return rampFill(heatmapStops(mode), norm)
}

// ─── The theme ──────────────────────────────────────────────────────────────

interface ThemeTokens extends ChartChrome {
  shadow: string
}

const TOKENS: Record<Mode, ThemeTokens> = {
  dark: { ...CHART_CHROME.dark, shadow: '0 12px 36px rgba(0,0,0,0.45)' },
  light: { ...CHART_CHROME.light, shadow: '0 12px 36px rgba(0,0,0,0.18)' },
}

/** Value / log / time axes share one shape: subtle gridlines, no axis line. */
function continuousAxis(t: ThemeTokens) {
  return {
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: { color: t.textSecondary, fontSize: 11, margin: 10, hideOverlap: true },
    // `nameTextStyle` is what styles `xAxis.name`, and leaving it out is why a
    // chart that named an axis had to restate a colour locally.
    nameTextStyle: { color: t.textSecondary, fontSize: 10 },
    splitLine: { lineStyle: { color: t.gridLine, type: 'dashed', width: 1 } },
  }
}

function buildTheme(mode: Mode) {
  const t = TOKENS[mode]
  return {
    backgroundColor: 'transparent',
    // Each theme carries its OWN series palette: one array cannot be correct
    // against both a white and a near-black canvas, and `brand.test.ts`
    // measures the two separately.
    color: [...CHART_SERIES[mode]],
    // The same face the MUI theme resolves to, so a chart label and the heading
    // above it are never two different fonts.
    textStyle: { fontFamily: SANS, color: t.textSecondary, fontSize: 11 },
    title: {
      textStyle: { color: t.textPrimary, fontSize: 13, fontWeight: 600, letterSpacing: -0.15 },
      subtextStyle: { color: t.textSecondary, fontSize: 11 },
    },
    legend: {
      textStyle: { color: t.textSecondary, fontSize: 11 },
      inactiveColor: t.textDisabled,
      icon: 'roundRect',
      itemWidth: 12,
      itemHeight: 8,
      pageTextStyle: { color: t.textSecondary },
      pageIconColor: t.textSecondary,
      pageIconInactiveColor: t.textDisabled,
    },
    grid: { top: 32, right: 16, bottom: 28, left: 8, containLabel: true },
    // Category axis carries the baseline and no gridlines.
    categoryAxis: {
      axisLine: { lineStyle: { color: t.axis, width: 1 } },
      axisTick: { show: false },
      axisLabel: { color: t.textSecondary, fontSize: 11, margin: 10, hideOverlap: true },
      nameTextStyle: { color: t.textSecondary, fontSize: 10 },
      splitLine: { show: false },
    },
    valueAxis: continuousAxis(t),
    logAxis: continuousAxis(t),
    timeAxis: continuousAxis(t),
    tooltip: {
      // An ECharts tooltip is an absolutely positioned div rendered INSIDE the
      // chart's own root, so it is clipped by any ancestor hiding its overflow
      // — and a chart in a bounded panel is surrounded by them. Without this an
      // `axis` tooltip over a many-series stack is cut in half by the frame it
      // is anchored in. Set here because it is a property of where these charts
      // LIVE, not of what any one of them plots.
      confine: true,
      backgroundColor: t.tooltipBg,
      borderColor: t.tooltipBorder,
      borderWidth: t.tooltipBorder === 'transparent' ? 0 : 1,
      padding: [8, 12],
      textStyle: { color: t.tooltipText, fontSize: 12, fontFamily: SANS },
      axisPointer: {
        type: 'line',
        lineStyle: { color: t.textDisabled, width: 1, type: 'dashed' },
        crossStyle: { color: t.textDisabled, width: 1, type: 'dashed' },
        label: {
          backgroundColor: t.tooltipBg,
          color: t.tooltipText,
          borderColor: t.tooltipBorder,
          fontSize: 11,
          padding: [4, 6],
        },
      },
      extraCssText: `border-radius: 6px; box-shadow: ${t.shadow};`,
    },
    axisPointer: { lineStyle: { color: t.axis } },
    // ECharts merges a theme's component entry as an OBJECT into every instance,
    // so an array here would reach nothing. Style keys only: `type` and
    // `filterMode` stay the caller's business.
    dataZoom: {
      textStyle: { color: t.textSecondary },
      borderColor: t.axis,
      handleStyle: { color: t.textDisabled, borderColor: t.axis },
      moveHandleStyle: { color: t.axis },
    },
    // ECharts 6 dropped the `radar.name` alias — only `radar.axisName` is read.
    radar: {
      axisName: { color: t.textSecondary },
      axisLine: { lineStyle: { color: t.gridLine } },
      splitLine: { lineStyle: { color: t.gridLine } },
    },
    visualMap: {
      textStyle: { color: t.textSecondary, fontSize: 11 },
      itemWidth: 12,
      itemHeight: 120,
      // The institutional diverging scale by default, rather than ECharts' own
      // blue-red, which belongs to no palette we own.
      inRange: { color: [...DIVERGING[mode]] },
    },
    parallel: {
      lineStyle: { color: CHART_SERIES[mode][0], opacity: 0.35 },
      axisLine: { lineStyle: { color: t.axis } },
      label: { color: t.textSecondary },
    },
    parallelAxis: {
      axisLine: { lineStyle: { color: t.axis } },
      axisTick: { lineStyle: { color: t.axis } },
      axisLabel: { color: t.textSecondary, fontSize: 10 },
      splitLine: { lineStyle: { color: t.gridLine, type: 'solid' } },
      nameTextStyle: { color: t.textSecondary, fontSize: 11 },
    },
    // Calm, and short enough that a wall display does not look busy.
    animationDuration: 280,
    animationEasing: 'cubicOut',
    animationDurationUpdate: 220,
    animationEasingUpdate: 'cubicOut',
  }
}

/**
 * Register both themes.
 *
 * Called once from `main.tsx`, at module load, BEFORE any React tree exists —
 * which is why every token above comes from a module constant and never from
 * the MUI `Theme`. A mode switch re-renders the charts with the other
 * registered name; it does not re-register anything.
 */
export function registerInternalEchartsThemes(): void {
  for (const mode of ['light', 'dark'] as const) {
    echarts.registerTheme(chartThemeName(mode), buildTheme(mode))
  }
}
